import {
  useGetRun,
  useGetRunCodeManifest,
} from '@/generated/hooks/jeeves/v1/api';
import type { V1Run, V1CodeManifest } from '@/generated/hooks/jeeves/models';

export type TTestRunManifestStatus = 'loading' | 'ready' | 'unavailable';

export interface IUseTestRunManifestResult {
  status: TTestRunManifestStatus;
  manifest: V1CodeManifest | undefined;
  run: V1Run | undefined;
}

interface IUseTestRunManifestParams {
  organizationId: string;
  workspaceId: string;
  automationId: string;
  runId: string | undefined;
}

/**
 * Resolves the `CodeManifest` a specific run actually executed, via jeeves'
 * `GetRunCodeManifest` (derived server-side from that run's own persisted
 * MAIN-phase code — never from whatever manifest is currently loaded in the
 * editor, and the raw code itself never reaches the browser). `test_automation`
 * reads staged, possibly since-edited `grimoire://aop.spy` with no
 * automation-version pointer on the run, so "the current manifest" can
 * silently mismatch an old (or a second, concurrently-started) run's actual
 * step structure.
 */
export function useTestRunManifest({
  organizationId,
  workspaceId,
  automationId,
  runId,
}: IUseTestRunManifestParams): IUseTestRunManifestResult {
  const enabled = !!runId;

  const { data: runResponse, isLoading: isRunLoading } = useGetRun(
    organizationId,
    workspaceId,
    automationId,
    runId ?? '',
    { query: { enabled } },
  );

  const {
    data: manifestResponse,
    isLoading: isManifestLoading,
    isError: isManifestError,
  } = useGetRunCodeManifest(
    organizationId,
    workspaceId,
    automationId,
    runId ?? '',
    { query: { enabled } },
  );

  const run = runResponse?.data;
  const manifest = manifestResponse?.data?.code_manifest;

  const status: TTestRunManifestStatus =
    !enabled || isRunLoading || isManifestLoading
      ? 'loading'
      : isManifestError || !manifest
        ? 'unavailable'
        : 'ready';

  return { status, manifest, run };
}
